import { isDepositInvoiceRole } from '../../utils/invoiceDeposits';
// Read-only display evidence. Never infer a historical invoice line from live order data.
const moneyCents = (value) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(number * 100) : null;
};

const asObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value) || {}; } catch (_) { return {}; }
};

const getServices = (payload) => {
  const source = asObject(payload);
  const rows = source?.OtherService ?? source?.otherServices ?? source?.otherService;
  return Array.isArray(rows) ? rows : [];
};

const serviceId = (row = {}) => String(
  row.originalOtherServiceId ?? row.ServiceId ?? row.serviceId ?? row.Id ?? row.id ?? ''
);

const shippingAmount = (row = {}) => {
  if (String(row?.Type ?? row?.type ?? '').toLowerCase() !== 'shipping') return null;
  const quantity = Number(row.Quantity ?? row.quantity ?? 1);
  const retail = Number(row.RetailPrice ?? row.retailPrice);
  const rate = Number(row.Taxes ?? row.taxes ?? row.taxRate);
  // The retail field is the commercial price; old Subtotal/TaxAmount aliases
  // may be stale. Require explicit valid price and tax rate for this comparison.
  if (!Number.isFinite(quantity) || quantity <= 0 ||
      !Number.isFinite(retail) || retail < 0 ||
      !Number.isFinite(rate) || rate < 0) return null;
  const subtotalCents = Math.round(retail * quantity * 100);
  const rateDecimal = rate > 1 ? rate / 100 : rate;
  const taxCents = Math.round(subtotalCents * rateDecimal);
  return { subtotalCents, taxCents, totalCents: subtotalCents + taxCents };
};

const invoiceSnapshotPayload = (invoice = {}) => {
  const relationships = asObject(invoice.invoiceRelationships);
  const render = asObject(relationships.renderSnapshot);
  return asObject(render.payload || (render.OtherService ? render : null));
};

// A paid or unpaid deposit is intentionally only part of a deposit/balance
// order. Never present the remaining balance as an invoice-vs-SO mismatch.
// Preserve mixed deposit + final-invoice cases for independent review.
export const hasOnlyDepositInvoices = (invoices = []) =>
  Array.isArray(invoices) && invoices.length > 0 &&
  invoices.every(isDepositInvoiceRole);

export const buildInvoiceDiscrepancy = ({ balanceStatus, invoices = [], order = {} } = {}) => {
  if (hasOnlyDepositInvoices(invoices)) return null;
  const differenceCents = moneyCents(balanceStatus?.uninvoicedAdditionalBalance);
  const salesOrderCents = moneyCents(balanceStatus?.currentOrderTotal);
  const coveredCents = moneyCents(balanceStatus?.activeInvoiceTotal);
  if (!balanceStatus?.hasUninvoicedAdditionalBalance || !differenceCents ||
      differenceCents <= 1 || !salesOrderCents || !coveredCents || coveredCents <= 0) return null;

  // A single invoice is identifiable only if it accounts for the coverage
  // reported by the authoritative balance endpoint. No guessing for deposits,
  // partial invoices, or multiple active invoices.
  const singleInvoice = invoices.length === 1 &&
    moneyCents(invoices[0]?.amount) === coveredCents ? invoices[0] : null;
  const salesOrders = Array.isArray(order?.SalesOrderData) ? order.SalesOrderData : [];
  const documentId = balanceStatus?.latestSalesOrderId;
  const salesOrder = documentId == null ? null : salesOrders.find((row) =>
    String(row.estimationId ?? row.id) === String(documentId)
  );
  const stamp = asObject(salesOrder?.stampedBookedAmount);
  const currentPayload = asObject(stamp?.cartSnapshot?.payload);
  const invoicePayload = singleInvoice ? invoiceSnapshotPayload(singleInvoice) : {};
  const frozenInvoiceServices = getServices(invoicePayload);
  const baseline = frozenInvoiceServices.length
    ? frozenInvoiceServices
    : (Array.isArray(order?.OtherService) ? order.OtherService : []);
  const baselineSource = frozenInvoiceServices.length ? 'invoice snapshot' : 'Order Cart';
  const currentServices = getServices(currentPayload);
  let shipping = null;

  // Display a shipping explanation only where precisely one matching line
  // accounts for the exact financial difference. It is a comparison, not proof
  // of who made the edit or whether it was approved.
  if (singleInvoice && currentServices.length && baseline.length) {
    const matching = [];
    for (const current of currentServices) {
      const id = serviceId(current);
      if (!id) continue;
      const previous = baseline.find((row) => serviceId(row) === id);
      if (!previous) continue;
      const before = shippingAmount(previous);
      const after = shippingAmount(current);
      if (!before || !after) continue;
      const changed = after.totalCents - before.totalCents;
      if (changed > 0) matching.push({ title: current.Title || current.title || 'Shipping',
        before, after, changed, source: baselineSource });
    }
    if (matching.length === 1 && Math.abs(matching[0].changed - differenceCents) <= 1) {
      shipping = matching[0];
    }
  }

  return {
    differenceCents,
    salesOrderCents,
    coveredCents,
    invoiceId: singleInvoice?.id ?? null,
    invoiceDate: singleInvoice?.invoiceDate || singleInvoice?.createdAt || null,
    invoiceCreatedAt: singleInvoice?.createdAt || null,
    salesOrderId: salesOrder?.estimationId ?? documentId ?? null,
    revisionNumber: stamp?.revisionNumber ?? null,
    refreshedAt: stamp?.regeneratedAt || null,
    refreshedBy: stamp?.regeneratedByName || null,
    refreshedByAvatar: stamp?.regeneratedByAvatar || null,
    shipping,
  };
};

export const displayMoney = (cents) => '$' + (Number(cents || 0) / 100).toFixed(2);
